import './Settings.scss'
import React, { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { onBackKeyPress, onEnterKeyPressAndClick, openMenuOnBackKey } from '@utils/helpers'
import { useTypedSelector } from '../../store'
import { ProfileButton } from '@components/Profile/ProfileButton/ProfileButton'
import { ProfileTitle } from '@components/Profile/ProfileTitle/ProfileTitle'
import { routes, settingsRoutes } from '@constants/routes.const'
import { setProfileButtonFocus } from '@actions/profileActions'
import { useDispatch } from 'react-redux'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import packageJSON from '../../../package.json'
import { VelvetSDK } from 'sdk'

interface SettingsProps {
  showMenu: (status: boolean) => void
}

export const Settings = ({ showMenu }: SettingsProps) => {
  const profileButtonRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { profile, menu } = useTypedSelector((state) => state)
  const { focusedButton } = profile
  const V = VelvetSDK.Vocabulary.vocabulary
  const { openMenu } = menu
  const version = V.releaseNumberLabel?.replace('%{numVersion}', packageJSON.version)

  const buttonData = [
    {
      buttonId: 'legal-button',
      buttonText: V.profilePreferencesLegalTitle,
      route: `${routes.SETTINGS}/${settingsRoutes.LEGAL}`,
    },
    {
      buttonId: 'logout-button',
      buttonText: V.profilePreferencesLogOut,
      route: `${routes.SETTINGS}/${settingsRoutes.LOGOUT}`,
      onFocusDown: '',
    },
  ]

  const handleNavigation = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent, route: string) => {
      e.preventDefault()
      onEnterKeyPressAndClick(e, () => navigate(route))
    },
    [navigate],
  )

  const onFocus = useCallback(
    (e: React.FocusEvent, focusableText: string): void => {
      e.preventDefault()
      dispatch(setProfileButtonFocus(focusableText))
    },
    [dispatch],
  )

  const closeSettingsPage = useCallback((e: KeyboardEvent) => onBackKeyPress(e, () => navigate(-1)), [navigate])

  useEffect(() => {
    showMenu(false)
  }, [showMenu])

  useEffect(() => {
    !openMenu && profileButtonRef.current?.focus()
  }, [openMenu])

  useEffect(() => {
    document.addEventListener('keydown', openMenuOnBackKey)
    document.addEventListener('keydown', closeSettingsPage)

    return () => {
      document.removeEventListener('keydown', openMenuOnBackKey)
      document.removeEventListener('keydown', closeSettingsPage)
      dispatch(setProfileButtonFocus(''))
    }
  }, [closeSettingsPage, dispatch])

  return (
    <div className="settings-page">
      <ProfileTitle title={V.menuItemSettings} />
      <div className="settings-buttons-container">
        {buttonData.map(({ buttonId, buttonText, route, onFocusDown }, idx) => {
          return (
            <ProfileButton
              key={buttonId}
              buttonType="large"
              buttonId={buttonId}
              buttonText={buttonText}
              onFocusEvent={(e) => onFocus(e, buttonText)}
              onKeyDownEvent={(e) => handleNavigation(e, route)}
              onClick={(e) => handleNavigation(e, route)}
              buttonRef={idx === 0 || focusedButton === buttonText ? profileButtonRef : null}
              onFocusDown={onFocusDown}
            />
          )
        })}
      </div>
      <Text tag={TextTag.P} className="release-version">
        {version ?? packageJSON.version}
      </Text>
    </div>
  )
}
